// outbox-ops.js — PURE entry builders for the offline write outbox. No I/O, no
// IndexedDB, no Medplum: each function returns a plain outbox-entry object that
// outbox.js enqueues and later replays. Keeping construction pure means the
// entry shape is defined in exactly one place and the engine never has to know
// what KIND of write it's replaying — it just rewrites local refs and POSTs.
//
// ── Entry shape (the durable row; see outbox-store.js for the DB) ──
//   {
//     id:          uuid — the row key
//     seq:         monotonic replay order (assigned by the store at enqueue)
//     op:          'create' | 'update'
//     resourceType:'Encounter' | 'Patient' | 'Observation' | …
//     body:        the FHIR resource to write (create) / the full updated
//                  resource (update). May carry urn:local:<uuid> references
//                  the engine rewrites to real UUIDs once the parent lands.
//     slug:        the OPD slug (stable business key) for conditional-create
//     slugSystem:  its identifier system (see SLUG_SYSTEMS below)
//     localRef:    'urn:local:<uuid>' — the placeholder OTHER entries reference
//                  to point at THIS not-yet-created resource; null for updates
//                  and for creates nothing depends on.
//     parentRefs:  ['urn:local:<uuid>', …] — local refs this entry's body
//                  depends on; the engine won't replay until all are resolved.
//     ifMatch:     the cached meta.versionId for op:'update' (optimistic lock),
//                  or null.
//     targetId:    the real resource id for op:'update', or null.
//     createdAt:   ISO timestamp of capture.
//     attempts:    replay attempt count (backoff/dead-letter accounting).
//     nextAttemptAt: epoch ms; the entry is actionable only when now >= this.
//     status:      'queued' | 'inflight' | 'done' | 'conflict' | 'failed'.
//     lastError:   last OperationOutcome/error text, or null.
//     resolvedId:  the real Medplum id once created (fills refMap for siblings).
//     profileId:   the enqueuing user's profile id — an entry is replayed ONLY
//                  by the same user (never replay one clinician's write as
//                  another; see outbox.js drain() auth gate).
//   }
//
// The slug systems below are copied verbatim from
// server/opd/medplum-store.mjs:76-81 (ID_SYSTEMS) so a client-queued write and a
// server-side mirror converge on the SAME conditional-create identifier — no
// twins across the disk/cloud boundary.

export const SLUG_SYSTEMS = Object.freeze({
  PATIENT: 'https://healther.io/opd/patient-slug',
  ENCOUNTER: 'https://healther.io/opd/encounter-slug',
  QR: 'https://healther.io/opd/qr-slug',
  RESOURCE: 'https://healther.io/opd/resource-slug',
  // The consult-draft Composition's stable per-encounter conditional-create key
  // (mirrors medplum-writes.js DRAFT_KEY verbatim) — one draft Composition per
  // encounter, so a first-save queued OFFLINE and replayed later can never twin.
  DRAFT: 'https://healther.io/opd/draft-key',
});

// ── Web-Crypto port of deterministicPatientId (server/opd/box-identity.mjs:37) ──
// Two boxes registering the SAME walk-in (offline) must compute the SAME patient
// slug so createResourceIfNoneExist collapses them to one Patient on replay —
// exactly the server's anti-twin guard, reimplemented with the async SubtleCrypto
// SHA-1 the browser has (Node's crypto.createHash is unavailable in the SPA).
//   sha1(phoneLast10 + '|' + normalizedName).slice(0,12), prefixed 'pat-'.
// Returns null when there isn't enough to be safe (phone < 10 digits or no
// name) — the caller then falls back to a random 'pat-'+uuid8 slug (a random
// id never wrong-merges two different humans), mirroring the server's null path.

function hex(buf) {
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

// A crypto handle that works both in the browser (window.crypto) and under Node
// (globalThis.crypto — Node ≥ 20 exposes the Web Crypto API globally).
function subtle() {
  const c = (typeof globalThis !== 'undefined' && globalThis.crypto) || null;
  if (!c || !c.subtle) throw new Error('Web Crypto (crypto.subtle) unavailable');
  return c.subtle;
}

export function randomUuid() {
  const c = (typeof globalThis !== 'undefined' && globalThis.crypto) || null;
  if (c && c.randomUUID) return c.randomUUID();
  // Fallback for the rare context without crypto.randomUUID.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/**
 * Deterministic patient slug from (phone last-10 + normalised name), async
 * because SubtleCrypto.digest is async. Returns null when unsafe.
 */
export async function deterministicPatientId({ phone, name }) {
  const ph = String(phone || '').replace(/\D/g, '').slice(-10);
  const nm = String(name || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (ph.length < 10 || !nm) return null;
  const data = new TextEncoder().encode(`${ph}|${nm}`);
  const digest = await subtle().digest('SHA-1', data);
  return 'pat-' + hex(digest).slice(0, 12);
}

/** A fresh local placeholder reference for a not-yet-created resource. */
export function localRefFor() {
  return 'urn:local:' + randomUuid();
}

// The common scaffold every builder shares — one place that stamps the durable
// bookkeeping fields so no builder can forget one.
function baseEntry({ op, resourceType, body, slug, slugSystem, localRef, parentRefs, ifMatch, targetId, profileId }) {
  return {
    id: randomUuid(),
    seq: 0, // assigned by the store at enqueue (nextSeq)
    op,
    resourceType,
    body,
    slug: slug || null,
    slugSystem: slugSystem || null,
    localRef: localRef || null,
    parentRefs: parentRefs || [],
    ifMatch: ifMatch || null,
    targetId: targetId || null,
    createdAt: new Date().toISOString(),
    attempts: 0,
    nextAttemptAt: 0,
    status: 'queued',
    lastError: null,
    resolvedId: null,
    profileId: profileId || null,
  };
}

/**
 * A patient-create entry. `slug` should come from deterministicPatientId (or a
 * random 'pat-'+uuid8 fallback) — the conditional-create key that collapses
 * twins. `localRef` (default a fresh one) is what a child Encounter/Observation
 * references via parentRefs to point at this patient before it has a real id.
 */
export function patientCreateEntry({ body, slug, profileId, localRef = localRefFor() }) {
  return baseEntry({
    op: 'create',
    resourceType: 'Patient',
    body,
    slug,
    slugSystem: SLUG_SYSTEMS.PATIENT,
    localRef,
    profileId,
  });
}

/**
 * An encounter-create entry. `parentRefs` typically carries the patient's
 * localRef (when the patient is also queued offline); `localRef` is what a
 * child Observation/Composition references.
 */
export function encounterCreateEntry({ body, slug, parentRefs = [], profileId, localRef = localRefFor() }) {
  return baseEntry({
    op: 'create',
    resourceType: 'Encounter',
    body,
    slug,
    slugSystem: SLUG_SYSTEMS.ENCOUNTER,
    localRef,
    parentRefs,
    profileId,
  });
}

/**
 * An intake-fact entry — a clinical Observation captured during intake. Keyed
 * on the generic resource-slug so a re-queued identical fact is idempotent.
 * `parentRefs` carries the encounter's (and/or patient's) localRef.
 */
export function intakeFactEntry({ body, slug, parentRefs = [], profileId, localRef = localRefFor() }) {
  return baseEntry({
    op: 'create',
    resourceType: body && body.resourceType ? body.resourceType : 'Observation',
    body,
    slug,
    slugSystem: SLUG_SYSTEMS.RESOURCE,
    localRef,
    parentRefs,
    profileId,
  });
}

/**
 * A consult-draft CREATE entry (op:'create') — the FIRST save of a consult-draft
 * Composition when none exists yet. Keyed on the DRAFT_KEY slug (the encounter id)
 * so replay is a conditional-create: a double-replay, or two boxes racing the
 * first save, converge on ONE draft Composition instead of a twin. `slug` is the
 * encounter id; `slugSystem` defaults to the shared DRAFT system. Mirrors the
 * update-path contract in medplum-writes.js so save works offline AND on.
 */
export function draftCreateEntry({ body, slug, slugSystem, parentRefs = [], profileId, localRef = localRefFor() }) {
  return baseEntry({
    op: 'create',
    resourceType: (body && body.resourceType) || 'Composition',
    body,
    slug,
    slugSystem: slugSystem || SLUG_SYSTEMS.DRAFT,
    localRef,
    parentRefs,
    profileId,
  });
}

/**
 * A generic conditional-create entry (op:'create') for a browser-direct clinical
 * write that needs offline durability + idempotent replay but is neither a
 * Patient/Encounter/intake-fact nor a consult-draft. Keyed on the caller-supplied
 * `slug`/`slugSystem` — the createResourceIfNoneExist identifier — so a
 * double-replay (or two boxes racing) converges on ONE resource. The body MUST
 * carry a matching FHIR `identifier` so Medplum's conditional search can find the
 * already-created copy. Used for the SACRED consult-transcript Media and a
 * doctor-confirmed AllergyIntolerance (both FHIR types that support identifier).
 */
export function resourceCreateEntry({ body, slug, slugSystem, parentRefs = [], profileId, localRef = localRefFor() }) {
  return baseEntry({
    op: 'create',
    resourceType: (body && body.resourceType) || 'Basic',
    body,
    slug,
    slugSystem: slugSystem || SLUG_SYSTEMS.RESOURCE,
    localRef,
    parentRefs,
    profileId,
  });
}

/**
 * An update entry (op:'update') — a full-resource PUT replayed with If-Match on
 * the cached versionId (optimistic concurrency). `targetId` is the real id;
 * `ifMatch` the versionId at read time. A stale If-Match parks the entry
 * 'conflict' at replay (the offline analogue of DraftConflictError).
 */
export function updateEntry({ resourceType, body, targetId, ifMatch, profileId }) {
  return baseEntry({
    op: 'update',
    resourceType,
    body,
    targetId,
    ifMatch,
    profileId,
  });
}
